/**
 * Room-scale “find the fault” challenge lifecycle.
 *
 * Owns electrical progress (jumpers + switches), the active authored fault,
 * and session-hold reset hooks. Does not implement grab, meshes, fan
 * animation, meter grab, or player locomotion.
 *
 * Hands: call {@link RoomChallenge.setConnections} after jumper snaps and
 * {@link RoomChallenge.setSwitch} from the wall switch; register
 * {@link RoomChallenge.registerSessionHoldReleaser} so session start/end can
 * drop sticky grabs without wiping circuit progress. On
 * {@link RoomChallenge.registerOnReset}, restore jumper meshes from
 * {@link RoomChallenge.getConnections} (healthy seating minus the open).
 *
 * Environment: poll {@link RoomChallenge.getState} / {@link RoomChallenge.isPowered}
 * (or {@link RoomChallenge.registerOnStateChange}) to drive lamps / indicators.
 * Optional HINT copy: {@link RoomChallenge.getActiveFaultHint} (conceptual only;
 * never the fault id).
 */

import {
  continuityDisplay,
  evaluateRoomCircuit,
  formatVoltageDisplay,
  pathExists,
  ROOM_DEVICE_IDS,
  SWITCH_LOAD_TEST_NODE_ID,
  type ContinuityDisplay,
  type MeterVoltageReading,
  type RoomCircuitState,
} from "../electrical/roomCircuit";
import { CONCEPT_VOLTAGE_V } from "../electrical/ohmsLaw";
import {
  FIRST_ROOM_FAULT,
  pickRoomFault,
  type RoomFault,
  type RoomFaultId,
} from "../electrical/roomFaults";

export type RoomChallengeHoldReleaser = () => void;
export type RoomChallengeResetListener = () => void;
export type RoomChallengeStateListener = (state: RoomCircuitState) => void;

function cloneConnections(
  pairs: ReadonlyArray<readonly [string, string]>,
): Array<[string, string]> {
  return pairs.map((pair) => [pair[0], pair[1]]);
}

function cloneSwitches(
  switches: Readonly<Record<string, boolean>>,
): Record<string, boolean> {
  return { ...switches };
}

export class RoomChallenge {
  private connections: Array<[string, string]> = [];
  private switches: Record<string, boolean> = {};
  private state: RoomCircuitState = evaluateRoomCircuit({
    connections: [],
    switches: {},
  });
  private activeFault: RoomFault = FIRST_ROOM_FAULT;

  private readonly holdReleasers: RoomChallengeHoldReleaser[] = [];
  private readonly resetListeners: RoomChallengeResetListener[] = [];
  private readonly stateListeners: RoomChallengeStateListener[] = [];

  constructor() {
    this.applyFault(FIRST_ROOM_FAULT, false);
  }

  /**
   * Pick a random authored fault (skips the last id when practical), apply
   * its jumpers / switches, emit state, and fire reset listeners so hands
   * can restore jumper meshes.
   */
  newFault(): RoomFaultId {
    const next = pickRoomFault(this.activeFault.id);
    this.applyFault(next, true);
    return next.id;
  }

  /**
   * Re-apply the CURRENT authored fault (same scenario). Does not pick a
   * new id. Does not move the player rig or clear XR input.
   * Hands/environment restore meshes via {@link registerOnReset}.
   */
  reset(): void {
    this.applyFault(this.activeFault, true);
  }

  /**
   * Hands registers grab/jumper drop here. Session start/end should call
   * {@link releaseSessionHolds} without resetting circuit progress.
   * Returns an unsubscribe function.
   */
  registerSessionHoldReleaser(fn: RoomChallengeHoldReleaser): () => void {
    this.holdReleasers.push(fn);
    return () => {
      const i = this.holdReleasers.indexOf(fn);
      if (i >= 0) this.holdReleasers.splice(i, 1);
    };
  }

  /** Drop held objects / phantom ownership. Does not undo seated jumpers. */
  releaseSessionHolds(): void {
    for (const fn of this.holdReleasers) {
      fn();
    }
  }

  /**
   * Hands/environment: restore jumper meshes and switch handles to match
   * {@link getConnections} / {@link getSwitches} after {@link reset} /
   * {@link newFault}.
   */
  registerOnReset(fn: RoomChallengeResetListener): () => void {
    this.resetListeners.push(fn);
    return () => {
      const i = this.resetListeners.indexOf(fn);
      if (i >= 0) this.resetListeners.splice(i, 1);
    };
  }

  /** Fired after evaluate (setConnections / setSwitch / reset / newFault). */
  registerOnStateChange(fn: RoomChallengeStateListener): () => void {
    this.stateListeners.push(fn);
    return () => {
      const i = this.stateListeners.indexOf(fn);
      if (i >= 0) this.stateListeners.splice(i, 1);
    };
  }

  /**
   * Replace the undirected jumper list (typically from a JumperSystem
   * collectConnections pass). Re-evaluates immediately.
   */
  setConnections(
    connections: ReadonlyArray<readonly [string, string]>,
  ): RoomCircuitState {
    this.connections = cloneConnections(connections);
    this.reevaluate();
    return this.state;
  }

  /**
   * Set a branch switch. Unknown ids are stored but only
   * known room switch ids currently gate devices.
   */
  setSwitch(id: string, closed: boolean): RoomCircuitState {
    this.switches[id] = closed;
    this.reevaluate();
    return this.state;
  }

  setSwitches(switches: Readonly<Record<string, boolean>>): RoomCircuitState {
    this.switches = cloneSwitches(switches);
    this.reevaluate();
    return this.state;
  }

  getConnections(): ReadonlyArray<readonly [string, string]> {
    return this.connections;
  }

  getSwitches(): Readonly<Record<string, boolean>> {
    return this.switches;
  }

  getState(): RoomCircuitState {
    return this.state;
  }

  getActiveFaultId(): RoomFaultId {
    return this.activeFault.id;
  }

  /** Conceptual hint for an optional HINT control — never the fault id. */
  getActiveFaultHint(): string | undefined {
    return this.activeFault.hint;
  }

  /**
   * True once the learner has restored missing jumper(s) (and the Light B
   * switch if they toggled it) so all five devices are powered. The sandbox
   * stays unlocked after this.
   */
  isFaultCleared(): boolean {
    return this.state.poweredCount >= this.state.totalCount;
  }

  isPowered(deviceId: string): boolean {
    return this.state.isPowered(deviceId);
  }

  /**
   * Live if `nodeId` is `srcP`, jumper-reachable from `srcP`, or the
   * Light B switch load jack while that lamp is powered.
   */
  nodeHasVoltage(nodeId: string): boolean {
    if (typeof nodeId !== "string" || nodeId.length === 0) return false;
    if (nodeId === SWITCH_LOAD_TEST_NODE_ID) {
      return this.state.isPowered("ceilingLightB");
    }
    return this.state.liveNodes.has(nodeId);
  }

  /** Undirected jumper continuity between two panel nodes. */
  pathExists(a: string, b: string): boolean {
    return pathExists(a, b, {
      connections: this.connections,
      switches: this.switches,
    });
  }

  meterReading(nodeId: string): MeterVoltageReading {
    return {
      kind: "voltage",
      volts: this.nodeHasVoltage(nodeId) ? CONCEPT_VOLTAGE_V : 0,
    };
  }

  formatMeterVoltage(nodeId: string): string {
    return formatVoltageDisplay(
      this.nodeHasVoltage(nodeId) ? CONCEPT_VOLTAGE_V : 0,
    );
  }

  formatContinuity(a: string, b: string): ContinuityDisplay {
    return continuityDisplay(a, b, {
      connections: this.connections,
      switches: this.switches,
    });
  }

  get poweredCount(): number {
    return this.state.poweredCount;
  }

  get totalCount(): number {
    return this.state.totalCount;
  }

  /** Progress fraction in [0, 1]. */
  get progressRatio(): number {
    const total = this.state.totalCount;
    if (total <= 0) return 0;
    return this.state.poweredCount / total;
  }

  /** HUD-friendly `poweredCount/totalCount`. Do not show the fault id. */
  get progressLabel(): string {
    return `${this.state.poweredCount}/${this.state.totalCount}`;
  }

  /**
   * Per-frame hook for later visuals. Circuit itself is event-driven via
   * setConnections / setSwitch — no grab or mesh work here.
   */
  update(_dt: number): void {
    // Electrical state is pushed from hands; environment may poll getState().
  }

  private applyFault(fault: RoomFault, fireReset: boolean): void {
    this.activeFault = fault;
    this.connections = cloneConnections(fault.connections);
    this.switches = cloneSwitches(fault.switches);
    this.state = evaluateRoomCircuit({
      connections: this.connections,
      switches: this.switches,
    });
    this.emitState();
    if (fireReset) {
      for (const listener of this.resetListeners) {
        listener();
      }
    }
  }

  private reevaluate(): void {
    this.state = evaluateRoomCircuit({
      connections: this.connections,
      switches: this.switches,
    });
    this.emitState();
  }

  private emitState(): void {
    const snapshot = this.state;
    for (const listener of this.stateListeners) {
      listener(snapshot);
    }
  }
}

/**
 * Shared room-scale challenge. Hands and environment import this instance
 * (do not construct a second copy).
 */
export const roomChallenge = new RoomChallenge();

/** Device count used by progress helpers (always {@link ROOM_DEVICE_IDS}.length). */
export const ROOM_CHALLENGE_DEVICE_COUNT = ROOM_DEVICE_IDS.length;
